import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { DealerAuthProvider, useDealerAuth } from "../../auth/DealerAuthProvider";
import { trackInventoryEvent } from "../../constants/automapDealerInventoryConstants";
import {
  AUTOMAP_DEALER_PORTAL_BASE,
  automapDealerPortalPath,
} from "../../constants/automapDealerPortalRoutes";
import { AUTOMAP_DEALER_PORTAL_I18N_MANIFEST } from "../../constants/automapDealerPortalI18n";
import { IntlProvider, useLocale } from "../../intl";
import DealerPortalLocaleControls from "./DealerPortalLocaleControls";
import "../../styles/dealerPortal.css";

function DealerPortalLayoutContent() {
  const location = useLocation();
  const { dealer, loading, logout, isAuthenticated } = useDealerAuth();
  const { t } = useLocale();

  useEffect(() => {
    trackInventoryEvent("inventory_page_viewed", { path: location.pathname });
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="dealer-portal">
        <main className="dealer-portal-main wrap">
          <p className="muted">{t("automotive.dealerPortal.loading")}</p>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={automapDealerPortalPath("login")} replace state={{ from: location }} />;
  }

  const dealerId = dealer.dealerId;
  const dealerName = dealer.dealerName || t("automotive.dealerPortal.defaultDealerName");
  const dealerSlug = dealer.dealerSlug || "";

  const navItems = [
    { key: "dashboard", path: automapDealerPortalPath("dashboard"), label: t("automotive.dealerPortal.nav.dashboard") },
    { key: "leads", path: automapDealerPortalPath("leads"), label: t("automotive.dealerPortal.nav.leads") },
    {
      key: "inventario",
      path: automapDealerPortalPath("inventario"),
      label: t("automotive.dealerPortal.nav.inventory"),
      isActive: (pathname) =>
        pathname.startsWith(`${AUTOMAP_DEALER_PORTAL_BASE}/inventario`) && !pathname.includes("/import"),
    },
    {
      key: "sponsorships",
      path: automapDealerPortalPath("patrocinios"),
      label: t("automotive.dealerPortal.nav.sponsorships"),
    },
    { key: "onboarding", path: automapDealerPortalPath("onboarding"), label: t("automotive.dealerPortal.nav.onboarding") },
    { key: "plan", path: automapDealerPortalPath("plan"), label: t("automotive.dealerPortal.nav.plan") },
    { key: "reportes", path: automapDealerPortalPath("reportes"), label: t("automotive.dealerPortal.nav.reports") },
    {
      key: "importaciones",
      path: automapDealerPortalPath("inventario/importaciones"),
      label: t("automotive.dealerPortal.nav.imports"),
      isActive: (pathname) => pathname.includes("/import"),
    },
  ];

  return (
    <div className="dealer-portal">
      {dealer?.impersonating ? (
        <div className="dealer-portal-impersonation">
          <div className="wrap dealer-portal-impersonation__inner">
            <span>
              Viewing dealer portal as <strong>{dealer.dealerName || dealer.name}</strong> for support
              and training.
            </span>
            <button type="button" className="dealer-portal-impersonation__exit" onClick={() => logout()}>
              {dealer?.adminReturnUrl ? "Exit to admin" : "Exit to admin console"}
            </button>
          </div>
        </div>
      ) : null}
      <header className="dealer-portal-header">
        <div className="wrap dealer-portal-header__inner">
          <div className="dealer-portal-header__brand">
            <p className="eyebrow">{t("automotive.dealerPortal.eyebrow")}</p>
            <h1>{dealerName}</h1>
            <p className="muted">{dealer.email}</p>
          </div>
          <div className="dealer-portal-header-actions">
            <DealerPortalLocaleControls />
            {dealerSlug ? (
              <Link to={`/v/automap/concesionarios/${dealerSlug}`}>
                {t("automotive.dealerPortal.publicProfile")}
              </Link>
            ) : null}
            <button type="button" className="cv-button-link" onClick={() => logout()}>
              {t("automotive.dealerPortal.signOut")}
            </button>
          </div>
        </div>
      </header>
      <div className="wrap">
        <nav className="dealer-portal-nav" aria-label={t("automotive.dealerPortal.mainNav")}>
          {navItems.map((item) => {
            const active = item.isActive
              ? item.isActive(location.pathname)
              : location.pathname.startsWith(item.path);
            return (
              <Link key={item.key} to={item.path} className={active ? "active" : undefined}>
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
      <main className="dealer-portal-main wrap">
        <Outlet context={{ dealerId, dealerName, dealerSlug }} />
      </main>
    </div>
  );
}

export default function DealerPortalLayout() {
  return (
    <IntlProvider surface="clientView" manifest={AUTOMAP_DEALER_PORTAL_I18N_MANIFEST}>
      <DealerAuthProvider>
        <DealerPortalLayoutContent />
      </DealerAuthProvider>
    </IntlProvider>
  );
}
